import { Component } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Title, BrowserModule } from '@angular/platform-browser';
import { downloadFile, fileNameFromHeaders } from 'src/app/utils/utils';
import { MenuItem, ConfirmationService, MessageService } from 'primeng/api';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Observable, finalize, firstValueFrom, map, of, throwError } from 'rxjs';
import {
  AgreementItem,
  AssetManagementItem,
  BaseResponse,
  DeliveryItem,
  DdtProductItem,
  EmptyResponse,
  EventContact,
  OrderCustomerItem,
  RecipientDto,
  ScheduleItem,
  TicketItem,
  WarehouseMovement,
  WarehouseMovementDetail,
  WorkflowTransitionEmailConfig,
} from 'src/app/api/models';
import {
  AddressBookService,
  AgreementsService,
  CommonService,
  DeliveryService,
  OrderService,
  SchedulazioniService,
  TicketService,
  WarehouseService,
} from 'src/app/api/services';
import { environment } from 'src/environments/environment';
import { enumTicketType } from 'src/app/shared/enums/ticket_type.enum';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';

interface BillingTypeItem {
  label: string;
  value: boolean | undefined;
}

interface MaterialeItem {
  codice: string;
  partNumber: string;
  descrizione: string;
  quantita: number;
  categoria: string;
}

interface SerialeItem {
  prodotto: string;
  serialNumber: string;
  asset: string;
}

@Component({
  selector: 'app-send-email-generic',
  templateUrl: './send-email-generic.component.html',
  styleUrls: ['./send-email-generic.component.scss'],
})
export class SendEmailGenericComponent {
  items: MenuItem[] | undefined;
  home: MenuItem | undefined;

  loading = false;

  detail?: ScheduleItem | OrderCustomerItem | WarehouseMovement | TicketItem | DeliveryItem | AgreementItem;
  agreement?: AgreementItem;
  selectedConfiguration?: Omit<WorkflowTransitionEmailConfig, 'to'> & {
    to: {
      to: EventContact[];
      cc: EventContact[];
      bcc: EventContact[];
    }[];
  };

  to: FormArray<
    FormGroup<{
      name: FormControl<string>;
      last_name: FormControl<string>;
      email: FormControl<string>;
    }>
  > = new FormArray<
    FormGroup<{
      name: FormControl<string>;
      last_name: FormControl<string>;
      email: FormControl<string>;
    }>
  >([]);
  cc: FormArray<
    FormGroup<{
      name: FormControl<string>;
      last_name: FormControl<string>;
      email: FormControl<string>;
    }>
  > = new FormArray<
    FormGroup<{
      name: FormControl<string>;
      last_name: FormControl<string>;
      email: FormControl<string>;
    }>
  >([]);
  bcc: FormArray<
    FormGroup<{
      name: FormControl<string>;
      last_name: FormControl<string>;
      email: FormControl<string>;
    }>
  > = new FormArray<
    FormGroup<{
      name: FormControl<string>;
      last_name: FormControl<string>;
      email: FormControl<string>;
    }>
  >([]);

  addContactForm = this.newContactForm();

  products: (DdtProductItem & { assets: AssetManagementItem[] })[] = [];
  showConfigurationError: boolean = false;
  selectedItem?: DdtProductItem & { assets: AssetManagementItem[] };
  showAssets = false;
  copySuccess = false;
  selectedForm?: 'to' | 'cc' | 'bcc';
  type?: 'schedule' | 'warehouse_load' | 'customer_order' | 'ticket' | 'delivery' | 'agreement';
  showAddContactForm: boolean = false;

  constructor(
    private readonly confirmationService: ConfirmationService,
    private readonly messageService: MessageService,
    private readonly commonService: CommonService,
    private readonly scheduleService: SchedulazioniService,
    private readonly orderService: OrderService,
    private readonly warehouseService: WarehouseService,
    private readonly addressBookService: AddressBookService,
    private readonly ticketService: TicketService,
    private readonly deliveryService: DeliveryService,
    private readonly agreementsService: AgreementsService,
    private readonly http: HttpClient,
    private readonly config: DynamicDialogConfig,
    private readonly ref: DynamicDialogRef,
    private readonly fb: FormBuilder,
    private readonly title: Title,
    private router: Router
  ) {}

  get schedule() {
    return this.detail as ScheduleItem;
  }
  get orderCustomer() {
    return this.detail as OrderCustomerItem;
  }
  get movement() {
    return this.detail as WarehouseMovement;
  }
  get ticket() {
    return this.detail as TicketItem;
  }
  get delivery() {
    return this.detail as DeliveryItem;
  }
  get agreementDetail() {
    return this.detail as AgreementItem;
  }

  ngOnInit() {
    this.type = this.config.data.type;
    this.detail = this.config.data.detail;

    // Ticket and delivery use a simplified flow: call previewBody directly
    if (this.type === 'ticket' || this.type === 'delivery') {
      this.initTicketOrDelivery();
      return;
    }

    // Agreement creation email
    if (this.type === 'agreement') {
      this.initAgreement();
      return;
    }

    let configuration: Observable<string> = of('');
    switch (this.type) {
      case 'schedule':
        this.title.setTitle(`${environment.softwareName} - Dettaglio schedulazione`);
        configuration = this.commonService
          .commonControllerGetScheduleConfiguration()
          .pipe(map(r => r.data));
        break;
      case 'customer_order':
        this.title.setTitle(`${environment.softwareName} - Dettaglio ordine cliente`);
        configuration = of(
          (this.detail as OrderCustomerItem).related_agreement
            ?.customer_order_email_configuration || ''
        );
        break;
      case 'warehouse_load':
        this.title.setTitle(`${environment.softwareName} - Dettaglio movimento di carico`);
        configuration = this.orderService
          .orderControllerGetOne({ id: (this.detail as WarehouseMovement).related_order_id })
          .pipe(map(res => res.data.related_agreement?.warehouse_load_email_configuration || ''));
        break;
    }
    configuration.subscribe({
      next: async data => {
        if (data && data !== '{}') {
          try {
            try {
              const configuration: WorkflowTransitionEmailConfig = JSON.parse(data);
              const contact_ids: Array<number> = configuration?.to.flatMap(el => [
                ...el.bcc,
                ...el.cc,
                ...el.to,
              ]);
              this.addressBookService
                .addressBookControllerGetAll({
                  contact_ids,
                  limit: contact_ids.length,
                  skip: 0,
                })
                .subscribe({
                  next: response => {
                    console.log(
                      (this.type === 'customer_order' &&
                        (this.detail as OrderCustomerItem).billing_related_registry &&
                        (this.detail as OrderCustomerItem).billing_related_registry.email && [
                          {
                            email: (this.detail as OrderCustomerItem).billing_related_registry
                              .email,
                          } as EventContact,
                        ]) ||
                        []
                    );
                    this.selectedConfiguration = {
                      ...configuration,
                      to: configuration.to.map(to => ({
                        to: [
                          ...to.to.map(
                            el => response.data.list.find(ec => ec.id === el) as EventContact
                          ),
                          ...((this.type === 'schedule' &&
                            (this.detail as ScheduleItem).assigned_user?.email && [
                              {
                                email: (this.detail as ScheduleItem).assigned_user!.email,
                                id: (this.detail as ScheduleItem).assigned_user!.id,
                                last_name: (this.detail as ScheduleItem).assigned_user!.last_name,
                                name: (this.detail as ScheduleItem).assigned_user!.name,
                                related_registry: (this.detail as ScheduleItem).related_registry,
                              } as EventContact,
                            ]) ||
                            (this.type === 'schedule' &&
                              (this.detail as ScheduleItem).related_registry?.email && [
                                {
                                  email: (this.detail as ScheduleItem).related_registry.email,
                                  id: (this.detail as ScheduleItem).related_registry.id,
                                  last_name: '',
                                  name: (this.detail as ScheduleItem).related_registry.name,
                                  related_registry: (this.detail as ScheduleItem).related_registry,
                                } as EventContact,
                              ]) ||
                            []),
                          ...((this.type === 'customer_order' &&
                            (this.detail as OrderCustomerItem).billing_related_registry &&
                            (this.detail as OrderCustomerItem).billing_related_registry.email && [
                              {
                                email: (this.detail as OrderCustomerItem).billing_related_registry
                                  .email,
                              } as EventContact,
                            ]) ||
                            []),
                          ...((this.type === 'warehouse_load' &&
                            (this.detail as WarehouseMovement).related_order &&
                            (this.detail as WarehouseMovement).related_order.related_registry &&
                            (this.detail as WarehouseMovement).related_order.related_registry
                              .email && [
                              {
                                email: (this.detail as WarehouseMovement).related_order
                                  .related_registry.email,
                              } as EventContact,
                            ]) ||
                            []),
                        ],
                        cc: to.cc.map(
                          el => response.data.list.find(ec => ec.id === el) as EventContact
                        ),
                        bcc: to.bcc.map(
                          el => response.data.list.find(ec => ec.id === el) as EventContact
                        ),
                      })),
                    };
                    if (this.type === 'schedule') {
                      this.scheduleService
                        .scheduleControllerPreviewEmailBody({
                          id: (this.detail as ScheduleItem)!.id,
                        })
                        .subscribe({
                          next: res => {
                            this.selectedConfiguration!.body = res.data.stringData;
                            this.selectedConfiguration!.subject =
                              res.data.subjectData ?? this.selectedConfiguration!.subject;
                          },
                          error: err => {},
                        });
                    }
                    if (this.type === 'customer_order') {
                      this.orderService
                        .orderCustomerControllerPreviewEmailBody({
                          id: (this.detail as OrderCustomerItem)!.id,
                        })
                        .subscribe({
                          next: res => {
                            this.selectedConfiguration!.body = res.data.stringData;
                            this.selectedConfiguration!.subject =
                              res.data.subjectData ?? this.selectedConfiguration!.subject;
                            // Indirizzi sottoscritti via notifiche email sul contatto: non
                            // fanno parte della configurazione salvata sulla commessa, li
                            // aggiungiamo qui ai destinatari già risolti sopra.
                            const subscribedRecipients: EventContact[] =
                              (res.data as any).recipientsData ?? [];
                            if (subscribedRecipients.length && this.selectedConfiguration?.to?.[0]) {
                              this.selectedConfiguration.to[0].to = [
                                ...this.selectedConfiguration.to[0].to,
                                ...subscribedRecipients,
                              ];
                            }
                          },
                          error: err => {},
                        });
                    }
                    if (this.type === 'warehouse_load') {
                      this.warehouseService
                        .warehouseControllerPreviewEmailBody({
                          id: (this.detail as WarehouseMovement)!.id,
                        })
                        .subscribe({
                          next: res => {
                            this.selectedConfiguration!.body = res.data.stringData;
                            this.selectedConfiguration!.subject =
                              res.data.subjectData ?? this.selectedConfiguration!.subject;
                          },
                          error: err => {},
                        });
                    }
                  },
                  error: err => {
                    this.messageService.add({
                      severity: 'error',
                      summary: 'Errore',
                      detail:
                        'Impossibile aprire la schermata di configurazione. Dati di configurazione corrotti sul database.',
                    });
                  },
                });
            } catch (error) {
              throw new Error('Impossibile inviare la mail. Configurazione non conforme.');
            }
          } catch (error) {
            throw new Error('Impossibile inviare la mail. Configurazione non conforme.');
          }
        }
      },
    });
  }

  private async initAgreement() {
    const id = (this.detail as AgreementItem).id;
    this.loading = true;
    try {
      const preview = await firstValueFrom(
        this.agreementsService.agreementsControllerPreviewBodyAgreement({ id })
      );
      const data = preview.data as any;
      const body: string | null = data?.body ?? null;
      const subject: string | null = data?.subject ?? null;
      if (!body) {
        this.loading = false;
        this.ref.close();
        return;
      }

      let to: { to: EventContact[]; cc: EventContact[]; bcc: EventContact[] }[] = [
        { to: [], cc: [], bcc: [] },
      ];
      try {
        const configRes = await firstValueFrom(
          this.commonService.commonControllerGetAgreementCreationConfiguration()
        );
        if (configRes.data) {
          const configuration: WorkflowTransitionEmailConfig = JSON.parse(configRes.data);
          const contact_ids = configuration.to.flatMap(el => [...el.to, ...el.cc, ...el.bcc]);
          if (contact_ids.length > 0) {
            const contacts = await firstValueFrom(
              this.addressBookService.addressBookControllerGetAll({
                contact_ids,
                limit: contact_ids.length,
                skip: 0,
              })
            );
            to = configuration.to.map(t => ({
              to: t.to
                .map(elId => contacts.data.list.find(c => c.id === elId) as EventContact)
                .filter(Boolean),
              cc: t.cc
                .map(elId => contacts.data.list.find(c => c.id === elId) as EventContact)
                .filter(Boolean),
              bcc: t.bcc
                .map(elId => contacts.data.list.find(c => c.id === elId) as EventContact)
                .filter(Boolean),
            }));
          }
        }
      } catch {
        // config non disponibile: destinatari vuoti
      }

      this.loading = false;
      this.selectedConfiguration = {
        attachments: [],
        body,
        subject: subject ?? '',
        to,
      };
    } catch {
      this.loading = false;
      this.ref.close();
    }
  }

  private initTicketOrDelivery() {
    const entityId =
      this.type === 'ticket' ? (this.detail as TicketItem).id : (this.detail as DeliveryItem).id;
    const statusId: number | undefined = this.config.data.statusId;
    const url = `${environment.baseUrl}/${this.type}/${entityId}/preview-body${statusId ? `?status_id=${statusId}` : ''}`;

    this.loading = true;
    this.http.get<any>(url).subscribe({
      next: res => {
        this.loading = false;
        const body: string | null = res?.data?.stringData ?? null;
        const subject: string | null = res?.data?.subjectData ?? null;
        const recipientsData: { to: EventContact[]; cc: EventContact[]; bcc: EventContact[] }[] =
          res?.data?.recipientsData ?? [{ to: [], cc: [], bcc: [] }];
        if (!body) {
          this.ref.close();
          return;
        }
        this.selectedConfiguration = {
          attachments: [],
          body,
          subject: subject ?? '',
          to: recipientsData.length ? recipientsData : [{ to: [], cc: [], bcc: [] }],
        };
      },
      error: () => {
        this.loading = false;
        this.ref.close();
      },
    });
  }

  sendEmail() {
    let message = "Sei sicuro di voler inviare l'email per ";
    switch (this.type) {
      case 'schedule':
        message += 'questa schedulazione?';
        break;
      case 'customer_order':
        message += 'questo ordine cliente?';
        break;
      case 'warehouse_load':
        message += 'questo movimento di carico?';
        break;
      case 'ticket':
        message += 'questo ticket?';
        break;
      case 'delivery':
        message += 'questa consegna?';
        break;
      case 'agreement':
        message += 'questa commessa?';
        break;
    }
    this.confirmationService.confirm({
      header: 'Conferma invio email',
      message,
      acceptLabel: 'Conferma',
      rejectLabel: 'Annulla',
      accept: () => {
        this.loading = true;
        const recipients = {
          to: [...this.mapInheritedContacts(this.selectedConfiguration!.to[0].to), ...this.to.value as RecipientDto[]],
          cc: [...this.mapInheritedContacts(this.selectedConfiguration!.to[0].cc), ...this.cc.value as RecipientDto[]],
          bcc: [...this.mapInheritedContacts(this.selectedConfiguration!.to[0].bcc), ...this.bcc.value as RecipientDto[]],
        };
        let call: Observable<
          BaseResponse & {
            'data'?: EmptyResponse;
          }
        > = throwError(() => new Error('Tipologia non supportata'));
        switch (this.type) {
          case 'schedule':
            call = this.scheduleService.scheduleControllerSendEmail({
              id: (this.detail as ScheduleItem).id,
              body: recipients,
            });
            break;
          case 'customer_order':
            call = this.orderService.orderCustomerControllerSendEmail({
              id: (this.detail as OrderCustomerItem).id,
              body: recipients,
            });
            break;
          case 'warehouse_load':
            call = this.warehouseService.warehouseControllerSendEmail({
              id: (this.detail as WarehouseMovement).id,
              body: recipients,
            });
            break;
          case 'ticket': {
            const id = (this.detail as TicketItem).id;
            const statusId: number | undefined = this.config.data.statusId;
            call = this.http.post<any>(
              `${environment.baseUrl}/ticket/${id}/send-email${statusId ? `?status_id=${statusId}` : ''}`,
              recipients
            );
            break;
          }
          case 'delivery': {
            const id = (this.detail as DeliveryItem).id;
            const statusId: number | undefined = this.config.data.statusId;
            call = this.http.post<any>(
              `${environment.baseUrl}/delivery/${id}/send-email${statusId ? `?status_id=${statusId}` : ''}`,
              recipients
            );
            break;
          }
          case 'agreement':
            call = this.agreementsService.agreementsControllerSendAgreementCreationEmail({
              id: (this.detail as AgreementItem).id,
              body: recipients,
            });
            break;
        }
        call.subscribe({
          next: response => {
            this.messageService.add({
              severity: 'success',
              summary: 'Invio email',
              detail: "L'email è stata inserita nella coda d'invio.",
            });
            this.loading = false;
            this.ref.close({ sent: true });
            switch (this.type) {
              case 'schedule':
                this.router.navigate(['/main', 'schedulazioni']);
                break;
              case 'customer_order':
                this.router.navigate(['/main', 'ordini-cliente']);
                break;
              case 'warehouse_load':
                this.router.navigate(['/main', 'movimenti']);
                break;
              case 'agreement':
                this.router.navigate(['/main', 'commesse']);
                break;
              // ticket/delivery: close only, no navigation (caller handles it)
            }
          },
          error: error => {
            this.loading = false;
            this.messageService.add({
              severity: 'error',
              summary: 'Invio email',
              detail: "Si è verificato un errore durante l'invio.",
            });
          },
        });
      },
    });
  }

  async generateReportPDFromTicket() {
    const t = (
      await firstValueFrom(
        this.ticketService.ticketControllerFindOne({ id: this.schedule!.related_ticket_id })
      )
    ).data;
    let docName = 'Ticket';
    if (t.ticket_type.id === enumTicketType.VERBALE) {
      docName = 'Verbale';
    }
    this.ticketService
      .ticketControllerDownloadOne$Response({ id: this.schedule!.related_ticket_id })
      .pipe(finalize(() => {}))
      .subscribe({
        next: value => {
          downloadFile(value.body, fileNameFromHeaders(value.headers, `${docName}_${t.ticket_number}_${t.ticket_subnumber}.pdf`));
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Download',
            detail: `Download del report fallito.`,
          });
        },
      });
  }

  removeInheritedContact(field: 'to' | 'cc' | 'bcc', index: number) {
    this.selectedConfiguration!.to[0][field].splice(index, 1);
    this.selectedConfiguration = { ...this.selectedConfiguration! };
  }

  private mapInheritedContacts(contacts: EventContact[]): RecipientDto[] {
    return contacts.map(c => ({
      name: c.name || '',
      last_name: c.last_name || '',
      email: c.email,
    })) as RecipientDto[];
  }

  showAddContact(form: 'to' | 'cc' | 'bcc') {
    this.selectedForm = form;
    this.addContactForm = this.newContactForm();
    this.showAddContactForm = true;
  }
  addContact() {
    if (this.addContactForm.invalid) {
      this.addContactForm.markAllAsTouched();
      return;
    }
    switch (this.selectedForm) {
      case 'to':
        this.to.push(this.addContactForm);
        break;
      case 'cc':
        this.cc.push(this.addContactForm);
        break;
      case 'bcc':
        this.bcc.push(this.addContactForm);
        break;
    }

    this.selectedForm = undefined;
    this.showAddContactForm = false;
  }
  private newContactForm(user?: { email: string; name: string; last_name: string }) {
    return new FormGroup<{
      name: FormControl<string>;
      last_name: FormControl<string>;
      email: FormControl<string>;
    }>({
      name: new FormControl<string>(user?.name || '', {
        nonNullable: true,
        validators: [],
      }),
      last_name: new FormControl<string>(user?.last_name || '', {
        nonNullable: true,
        validators: [],
      }),
      email: new FormControl<string>(user?.email || '', {
        nonNullable: true,
        validators: [Validators.required, Validators.email],
      }),
    });
  }
}
